import { useState, useEffect } from 'react'
import { fetchOfferings, purchasePackage, isNativePlatform } from '../services/revenueCat'
import { auth } from '../firebase'
import { IconX, IconTicket, IconStar } from './icons'

// `identifier` matches the package identifier configured in the RevenueCat
// dashboard's default Offering. Keep these strings in sync with the dashboard.
const TIERS = [
  { tickets: 5,   price: 0.99,  name: 'Walk-On Pack',     identifier: 'walk_on_pack' },
  { tickets: 12,  price: 1.99,  name: 'Bit Part',         identifier: 'bit_part'     },
  { tickets: 30,  price: 4.99,  name: 'Co-Star',          identifier: 'co_star', featured: true },
  { tickets: 65,  price: 9.99,  name: 'Headliner',        identifier: 'headliner'    },
  { tickets: 120, price: 19.99, name: 'Box Office Smash', identifier: 'box_office_smash' },
]

export default function TicketStore({ tickets, onPurchase, onClose }) {
  const [toast, setToast]       = useState(null)
  const [pendingId, setPending] = useState(null)   // identifier of in-flight purchase

  // Pre-fetch offerings on mount so the Apple sheet is ready to pop instantly
  // on the first Buy tap. Logs package count for verification.
  useEffect(() => {
    fetchOfferings()
  }, [])

  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2200)
  }

  const handleBuy = async (tier) => {
    if (pendingId) return                          // ignore double-taps mid-purchase
    setPending(tier.identifier)

    // ── Web (non-native): Stripe Checkout ───────────────────────────────────
    // RevenueCat's Capacitor SDK is iOS-only. In the browser we create a Stripe
    // Checkout session server-side and redirect to Stripe's hosted page. Tickets
    // are credited by /api/stripe-webhook after payment completes, so there's no
    // onPurchase() call here — the redirect navigates away from the app.
    if (!isNativePlatform()) {
      // Anonymous "guests" carry a Firebase uid (so they can join parties) but
      // are NOT real accounts — they still can't buy tickets. Require a real,
      // non-anonymous login here.
      const fbUser = auth.currentUser
      if (!fbUser || fbUser.isAnonymous) {
        setPending(null)
        showToast('Please sign in to buy tickets.')
        return
      }
      const uid = fbUser.uid
      try {
        const res = await fetch('/api/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tierId: tier.identifier,
            tickets: tier.tickets,
            price: tier.price,
            name: tier.name,
            uid,
          }),
        })
        if (!res.ok) throw new Error(`checkout session request failed: ${res.status}`)
        const { url } = await res.json()
        if (!url) throw new Error('no checkout URL returned')
        window.location.href = url   // redirect to Stripe-hosted checkout
        return                       // leave pending set — we're navigating away
      } catch (err) {
        console.error('[stripe] checkout failed:', err)
        setPending(null)
        showToast('Purchase failed. Please try again.')
        return
      }
    }

    // ── Native (iOS): RevenueCat — unchanged ────────────────────────────────
    const result = await purchasePackage(tier.identifier)
    setPending(null)

    if (result.status === 'success') {
      // Hand off to App.jsx's existing handlePurchase — which updates the
      // ticket count via setTickets, syncing to Firestore.
      onPurchase(tier.tickets)
      showToast(`+${tier.tickets} Tickets Added!`)
      return
    }

    if (result.status === 'cancelled') {
      // User cancelled the Apple sheet — silent. Nothing destructive.
      return
    }

    if (result.status === 'skipped') {
      // Browser dev path: no-op. The service already logged the warning.
      return
    }

    // Real error — let the user know without blocking the UI.
    console.error('[RC] purchase error surfaced to UI:', result.error)
    showToast('Purchase failed. Please try again.')
  }

  return (
    <div className="store-overlay" onClick={onClose}>
      <div className="store-panel" onClick={(e) => e.stopPropagation()}>
        <button className="store-close btn-ghost" onClick={onClose} aria-label="Close store"><IconX size={16} /></button>

        <div className={`store-toast-wrap ${toast ? 'store-toast-wrap--visible' : ''}`}>
          {toast}
        </div>

        <div className="store-header">
          <h2 className="store-title">Get Tickets <IconTicket size={18} style={{ verticalAlign: 'middle' }} /></h2>
          <p className="store-subtitle">Keep the show running</p>
          <p className="store-balance">You have <strong>{tickets}</strong> <IconTicket size={14} style={{ verticalAlign: 'middle' }} /></p>
        </div>

        <div className="store-tiers">
          {TIERS.map((tier) => {
            const isPending = pendingId === tier.identifier
            return (
              <div
                key={tier.identifier}
                className={`store-tier ${tier.featured ? 'store-tier--featured' : ''}`}
              >
                {tier.featured && <span className="store-tier__badge"><IconStar size={12} style={{ verticalAlign: 'middle' }} /> Best Value</span>}
                <div className="store-tier__details">
                  <span className="store-tier__name">{tier.name}</span>
                  <div className="store-tier__info">
                    <span className="store-tier__count">{tier.tickets} <IconTicket size={13} style={{ verticalAlign: 'middle' }} /></span>
                    <span className="store-tier__price">${tier.price.toFixed(2)}</span>
                  </div>
                </div>
                <button
                  className="btn-primary store-tier__buy"
                  onClick={() => handleBuy(tier)}
                  disabled={!!pendingId}
                >
                  {isPending ? '…' : 'Buy'}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
